"use client"

import { useEffect, useState } from "react"
import { motion } from "framer-motion"

interface GenerationLoadingComponentProps {
  onComplete?: () => void
  duration?: number // in seconds
  message?: string
  progress?: number
  showText?: boolean
  estimatedDuration?: number // in milliseconds
}

export function GenerationLoadingComponent({
  onComplete,
  duration = 7,
  message = "Bringing your design to life...", // Updated default message text
  progress,
  showText = true,
  estimatedDuration,
}: GenerationLoadingComponentProps) {
  const [currentProgress, setCurrentProgress] = useState(progress || 0)
  const [estimatedProgress, setEstimatedProgress] = useState(0)

  useEffect(() => {
    if (progress === undefined && onComplete) {
      const interval = setInterval(() => {
        setCurrentProgress((prev) => {
          const newProgress = prev + 100 / (duration * 10)
          return newProgress >= 100 ? 100 : newProgress
        })
      }, 100)

      const timer = setTimeout(() => {
        if (typeof onComplete === "function") {
          onComplete()
        }
      }, duration * 1000)

      return () => {
        clearInterval(interval)
        clearTimeout(timer)
      }
    }
  }, [duration, onComplete, progress])

  useEffect(() => {
    if (estimatedDuration && estimatedDuration > 0) {
      const startTime = Date.now()
      const maxProgress = 98 // Increased cap to 98%

      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime
        const timeProgress = (elapsed / estimatedDuration) * 100 // Percentage of time elapsed

        let displayProgress
        // Then 70-98% display happens in last 50% of time (slow)
        if (timeProgress <= 50) {
          // First 50% of time: progress from 0% to 70%
          displayProgress = (timeProgress / 50) * 70
        } else {
          // Last 50% of time: progress from 70% to 98% with easing
          const timeInSecondHalf = timeProgress - 50
          const progressInSecondHalf = timeInSecondHalf / 50 // 0 to 1
          const easedProgress = Math.pow(progressInSecondHalf, 1.5) // Ease out
          displayProgress = 70 + easedProgress * 28 // 28 = 98 - 70
        }

        // Cap at maxProgress
        const cappedProgress = Math.min(displayProgress, maxProgress)
        setEstimatedProgress(cappedProgress)
      }, 100)

      return () => clearInterval(interval)
    }
  }, [estimatedDuration])

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] space-y-6">
      <div className="relative">
        <motion.div
          className="w-40 h-40 flex items-center justify-center border-2 border-gray-200 rounded-xl p-4 bg-white shadow-sm"
          animate={{
            scale: [1, 1.05, 1],
          }}
          transition={{
            repeat: Number.POSITIVE_INFINITY,
            duration: 2,
            ease: "easeInOut",
          }}
        >
          <img
            src="/images/design-mode/bedroom_sequence.gif"
            alt="Room generation animation"
            className="w-full h-full object-contain"
          />
        </motion.div>
      </div>

      {showText && (
        <div className="text-center space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">{message}</h3>
          <p className="text-sm text-gray-600">
            Please do not navigate away from this page while we create your design.
            <br />
            This may take a few moments.
          </p>
        </div>
      )}

      {estimatedDuration && estimatedDuration > 0 && (
        <div className="flex flex-col items-center space-y-2">
          <p className="text-sm text-gray-600">{Math.round(estimatedProgress)}% complete</p>
          <div className="w-80 bg-gray-200 rounded-full h-2">
            <div
              className="bg-[#40A1AB] h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${estimatedProgress}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex space-x-2">
        <div className="w-2 h-2 bg-[#40A1AB] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
        <div className="w-2 h-2 bg-[#40A1AB] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
        <div className="w-2 h-2 bg-[#40A1AB] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
      </div>

      {progress !== undefined && (
        <div className="w-64 bg-gray-200 rounded-full h-2">
          <div
            className="bg-[#40A1AB] h-2 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${Math.min(100, Math.max(0, currentProgress))}%` }}
          />
        </div>
      )}
    </div>
  )
}

export default GenerationLoadingComponent
